import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
// @ts-ignore
import { Document, Page, pdfjs } from "react-pdf";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState, useRef } from "react";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface PDFViewerModalProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  authToken?: string | null;
}

const PDFViewerModal: React.FC<PDFViewerModalProps> = ({
  open,
  onClose,
  fileUrl,
  authToken,
}) => {
  const [pageWidth, setPageWidth] = useState<number>(0);
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setPageWidth(Math.min(containerWidth, 680));
      }
    });

    if (containerRef.current && open) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [open]);

  // Fetch image with authentication and create blob URL
  useEffect(() => {
    if (!open || !fileUrl || fileUrl.toLowerCase().endsWith(".pdf")) {
      return;
    }

    let isMounted = true;

    const fetchImage = async () => {
      try {
        const response = await fetch(fileUrl, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });

        if (!response.ok) {
          throw new Error("Failed to fetch image");
        }

        const blob = await response.blob();
        if (isMounted) {
          const blobUrl = URL.createObjectURL(blob);
          setImageBlobUrl(blobUrl);
        }
      } catch (error) {
        console.error("Error fetching image:", error);
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
      if (imageBlobUrl) {
        URL.revokeObjectURL(imageBlobUrl);
        setImageBlobUrl(null);
      }
    };
  }, [open, fileUrl, authToken]);

  const isPDF = fileUrl.toLowerCase().endsWith(".pdf");

  // Prepare file configuration with authentication headers
  const fileConfig = authToken
    ? {
        url: fileUrl,
        httpHeaders: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    : fileUrl;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "680px",
          width: "100%",
          margin: "auto",
        },
      }}
    >
      <DialogTitle>
        Tiedoston esikatselu
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers ref={containerRef}>
        {isPDF ? (
          <Document file={fileConfig} loading="Ladataan PDF-tiedostoa...">
            <Page
              pageNumber={1}
              width={pageWidth > 0 ? pageWidth - 32 : undefined}
            />
          </Document>
        ) : imageBlobUrl ? (
          <img
            src={imageBlobUrl}
            alt="Esikatseltava tiedosto"
            style={{
              maxWidth: "100%",
              height: "auto",
              display: "block",
              margin: "0 auto",
            }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Ladataan kuvaa...
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PDFViewerModal;
